import {
  area,
  Feature,
  Polygon,
  polygon,
  simplify,
  truncate,
} from '@turf/turf';
import { keepMainCluster } from 'app/utils/field-boundary/clustering';
import {
  fillHoles,
  joinNearbyBlocks,
} from 'app/utils/field-boundary/components';
import {
  createGrid,
  gridToPosition,
  rasterizePolygon,
} from 'app/utils/field-boundary/grid';
import {
  closeGaps,
  removeTransitCorridors,
} from 'app/utils/field-boundary/morphology';
import {
  smoothStaircase,
  traceOuterRing,
} from 'app/utils/field-boundary/outline';
import { MIN_RING_VERTICES } from 'app/utils/field-boundary/shared';
import {
  collectCoveragePolygons,
  sanitizePath,
  toClosedRing,
} from 'app/utils/field-boundary/task-data';
import {
  DEFAULT_FIELD_BOUNDARY_SUGGESTION_OPTIONS,
  DeviceBoundary,
  FieldBoundaryCoverageTask,
  FieldBoundarySuggestion,
  FieldBoundarySuggestionOptions,
  FieldBoundarySuggestionSource,
  FieldBoundarySuggestionTask,
  LatLngPoint,
} from 'app/utils/field-boundary/types';

export {
  collectCoveragePolygons,
  implementWidthFromIsobusStages,
  implementWidthFromSteps,
} from 'app/utils/field-boundary/task-data';
export * from 'app/utils/field-boundary/types';

/** About 1 cm; keeps the geometry compact. */
const OUTPUT_COORDINATE_PRECISION = 7;

/**
 * Suggests a field boundary from a processed task, to prefill the field
 * creation screen.
 *
 * @remarks
 * The boundary recorded on the device wins over the coverage because it is
 * the one the operator actually mapped. The coverage-derived boundary follows
 * where the implement worked with sections on: it stops at the implement edge
 * (give or take one raster cell), misses headlands and strips that were not
 * worked, and only reflects what this single task worked. It is therefore
 * always a suggestion for the user to review on the map, never saved as is.
 *
 * Driving to and from the field with the sections on also leaves trails. They
 * are dropped when the rate shows nothing was applied, and, when the implement
 * width is known, stretches about one implement wide are cut off as transit
 * corridors.
 *
 * A field worked in strips leaves several blocks apart from each other.
 * Blocks close enough are joined into one boundary, filling the strips left
 * between them; farther ones are taken as other fields and left out.
 *
 * Tasks processed before this feature need nothing new: both sources are read
 * from data every parsed task already has, and missing or empty fields yield
 * `null`, so callers keep the manual drawing flow.
 */
export function suggestFieldBoundary(
  task: FieldBoundarySuggestionTask | null | undefined,
  options: Partial<FieldBoundarySuggestionOptions> = {},
): FieldBoundarySuggestion | null {
  return suggestFieldBoundaryFromCoverage(
    {
      boundaries: task?.boundaries,
      coveragePolygons: collectCoveragePolygons(task?.trails),
      implementWidthMeters: task?.implementWidthMeters,
    },
    options,
  );
}

/**
 * Same as `suggestFieldBoundary`, for a task whose trails were already reduced
 * to the worked polygons (see `collectCoveragePolygons`).
 */
export function suggestFieldBoundaryFromCoverage(
  task: FieldBoundaryCoverageTask,
  options: Partial<FieldBoundarySuggestionOptions> = {},
): FieldBoundarySuggestion | null {
  const settings = { ...DEFAULT_FIELD_BOUNDARY_SUGGESTION_OPTIONS, ...options };

  const deviceBoundary = boundaryFromDeviceBoundaries(
    task.boundaries,
    settings.minAreaSquareMeters,
  );
  if (deviceBoundary) {
    return {
      source: FieldBoundarySuggestionSource.DEVICE_BOUNDARY,
      feature: roundCoordinates(deviceBoundary),
    };
  }

  const coverageBoundary = boundaryFromCoverage(
    task.coveragePolygons,
    settings,
    task.implementWidthMeters,
  );
  if (coverageBoundary) {
    return {
      source: FieldBoundarySuggestionSource.COVERAGE,
      feature: roundCoordinates(coverageBoundary),
    };
  }

  return null;
}

/**
 * Picks the largest boundary recorded on the device.
 *
 * @remarks
 * The largest one is the outer boundary; smaller ones are inner exclusions or
 * leftovers. Holes are not carried over — AGP boundaries already arrive
 * without them — and the user can draw them on the creation screen.
 */
export function boundaryFromDeviceBoundaries(
  boundaries: DeviceBoundary[] | null | undefined,
  minAreaSquareMeters = DEFAULT_FIELD_BOUNDARY_SUGGESTION_OPTIONS.minAreaSquareMeters,
): Feature<Polygon> | null {
  let largest: Feature<Polygon> | null = null;
  let largestArea = minAreaSquareMeters;

  if (Array.isArray(boundaries)) {
    boundaries.forEach((boundary) => {
      const ring = toClosedRing(boundary?.path);
      if (ring) {
        const candidate = polygon([ring]);
        const candidateArea = area(candidate);
        if (candidateArea >= largestArea) {
          largest = candidate;
          largestArea = candidateArea;
        }
      }
    });
  }

  return largest;
}

/**
 * Builds a boundary from the worked area.
 *
 * Pipeline: rasterize the trail polygons on a metric grid, close the narrow
 * unworked strips between passes (morphological closing), cut off transit
 * corridors, fill holes, join the blocks close to each other into one area,
 * trace its outline, smooth its staircase into straight segments and
 * simplify it.
 */
export function boundaryFromCoverage(
  polygons: LatLngPoint[][],
  options: Partial<FieldBoundarySuggestionOptions> = {},
  implementWidthMeters?: number | null,
): Feature<Polygon> | null {
  const settings = { ...DEFAULT_FIELD_BOUNDARY_SUGGESTION_OPTIONS, ...options };
  const validPolygons = keepMainCluster(
    polygons
      .map(sanitizePath)
      .filter((path) => path.length >= MIN_RING_VERTICES),
    settings,
  );

  if (validPolygons.length === 0) {
    return null;
  }

  const grid = createGrid(validPolygons, settings);
  validPolygons.forEach((path) => rasterizePolygon(grid, path));
  closeGaps(grid);
  // Corridors are cut before filling holes: a corridor that leaves the field
  // and comes back encloses unworked land, which would otherwise be filled
  // into a solid block that is no longer thin enough to cut.
  removeTransitCorridors(grid, implementWidthMeters);
  fillHoles(grid);
  joinNearbyBlocks(grid, settings.maxJoinGapMeters);
  fillHoles(grid);

  const ring = traceOuterRing(grid);
  if (!ring) {
    return null;
  }

  // Simplified in grid units, where distances are isotropic, so the tolerance
  // means the same in meters along any direction. Below one cell it would keep
  // raster noise, hence the floor.
  const toleranceMeters = Math.max(
    settings.simplifyToleranceMeters,
    grid.cellSizeMeters,
  );
  const simplified = simplify(polygon([smoothStaircase(ring)]), {
    tolerance: toleranceMeters / grid.cellSizeMeters,
    highQuality: true,
  });
  const boundary = polygon([
    simplified.geometry.coordinates[0].map((position) =>
      gridToPosition(grid, position),
    ),
  ]);

  if (area(boundary) < settings.minAreaSquareMeters) {
    return null;
  }
  return boundary;
}

function roundCoordinates(feature: Feature<Polygon>): Feature<Polygon> {
  return truncate(feature, { precision: OUTPUT_COORDINATE_PRECISION });
}
